/**
 * 토스트 알림 — 토스 스타일 하단 중앙 스낵바
 *
 * 공개 API (기존 호출부와 100% 호환):
 *   const { showToast, removeToast, clearToasts } = useToast();
 *   showToast(message, type = 'info', duration, action, options)
 *   - type     : 'info' | 'success' | 'warning' | 'error' (그 외 값은 info로 처리)
 *   - duration : ms. 0이면 사용자가 닫을 때까지 유지. 생략하면 타입별 기본값
 *                (success/info 2.5초, warning/error 4초 + 긴 문장은 읽을 시간만큼 조금 더, 최대 +3초)
 *   - action   : { label, onClick } — 오른쪽 텍스트 버튼. 눌러도 토스트는 유지한다
 *                ("다시 열기"처럼 반복 실행이 필요한 액션을 위해. 커밋 1d920d60c 참고)
 *   - options  : { persistAcrossRoute, bypassLayerQueue, replaceKey }
 *                persistAcrossRoute — 페이지가 바뀌어도 지우지 않는다 (기본 false)
 *                bypassLayerQueue   — 모달이 열려 있어도 바로 띄운다 (기본 false)
 *                replaceKey         — 같은 키의 토스트(보이는 것·대기열)를 지우고 새 토스트로 바꾼다.
 *                                     연달아 누르는 버튼의 안내가 겹쳐 쌓이지 않게 할 때 쓴다.
 *   clearToasts({ includePersistent, keepNewerThanMs }) — 보이는 토스트와 대기열을 비운다
 *
 * 레이어 정책 (LayerProvider가 있을 때):
 *   - 다이얼로그·바텀시트가 하나라도 열려 있으면 error가 아닌 토스트는 대기열에 보관한다.
 *   - 마지막 레이어가 닫히면 대기열의 가장 최근 1개만 보여준다 (모달 뒤에 쌓인 알림을 한꺼번에 쏟지 않는다).
 *   - error 토스트는 모달 위에서도 바로 보인다 (사용자가 지금 알아야 하는 실패).
 *
 * 라우트 정책: RouteToastCleaner(components/common)가 경로가 바뀔 때 clearToasts()를 호출한다.
 *   persistAcrossRoute 토스트와, 이동 직전에 띄운 토스트(예: "저장했어요" → navigate)는 남긴다.
 *
 * 메시지 정리 규칙 (sanitizeToastMessage):
 *   - 문장 앞뒤의 이모지·픽토그램(✅ ❌ 🎉 ⚠️ ✓ …)은 제거한다. 타입 아이콘이 그 역할을 대신한다.
 *   - 문장 중간의 이모지는 앞뒤가 모두 공백일 때만("채팅 📎 메뉴") 제거한다.
 *     조사가 붙어 있으면("📎로") 문장이 깨지므로 그대로 둔다.
 *   - 한글·숫자·일반 문장부호(· • → ※ 등)는 건드리지 않는다. 줄바꿈은 유지한다.
 *   - 이모지만 있던 줄은 버리고, 정리 후 내용이 없으면 토스트를 띄우지 않는다.
 *
 * 인터랙션:
 *   - 본문 탭/클릭으로 닫힘 (액션 버튼이 있는 토스트는 실수로 액션을 잃지 않도록 제외)
 *   - 아래로 스와이프해서 닫힘, 마우스 호버·포커스 중에는 자동 닫힘 타이머 일시정지
 *   - 최대 3개까지 쌓이며, 새 토스트가 위로 올라온다. 넘치면 가장 오래된 것부터 사라진다.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLayer } from './common/LayerContext';
import './Toast.css';

const TOAST_TYPES = ['info', 'success', 'warning', 'error'];

const DEFAULT_DURATION_MS = {
  success: 2500,
  info: 2500,
  warning: 4000,
  error: 4000,
};

// 20자까지는 한눈에 읽히는 길이로 보고, 그 이상은 글자당 60ms(최대 3초)를 더 준다.
const READ_TIME_GLANCE_CHARS = 20;
const READ_TIME_MS_PER_CHAR = 60;
const READ_TIME_MAX_EXTRA_MS = 3000;

export const MAX_VISIBLE_TOASTS = 3;
const EXIT_ANIMATION_MS = 150;
const SWIPE_DISMISS_PX = 48;
const RESUME_MIN_MS = 800;

// ---------------------------------------------------------------------------
// 메시지 정리 (이모지 제거)
// ---------------------------------------------------------------------------

// 이모지·픽토그램 본체: 유니코드 Extended_Pictographic + Emoji_Presentation,
// Misc Symbols/Dingbats 블록(☆ ✓ ✗ ✦ 등 장식 기호), 국기용 Regional Indicator.
// 한글·한자·숫자·일반 문장부호(· • → ※ …)는 포함되지 않는다.
const PICTOGRAPHIC =
  '\\p{Extended_Pictographic}\\p{Emoji_Presentation}\\u2600-\\u27BF\\u{1F1E6}-\\u{1F1FF}';
// 이모지 시퀀스 구성 요소: ZWJ, variation selector, keycap, 피부색 modifier, tag 문자.
const EMOJI_MODIFIERS = '\\u200D\\uFE0E\\uFE0F\\u20E3\\u{1F3FB}-\\u{1F3FF}\\u{E0020}-\\u{E007F}';
const EMOJI_CHARS = `${PICTOGRAPHIC}${EMOJI_MODIFIERS}`;
const SPACES = ' \\t\\u00A0';

// 중첩 수량자 없이 문자 클래스 1개만 반복 → 긴 문장에서도 선형 시간에 처리된다.
const LEADING_EMOJI_RE = new RegExp(`^[${EMOJI_CHARS}${SPACES}]+`, 'u');
const TRAILING_EMOJI_RE = new RegExp(`[${EMOJI_CHARS}${SPACES}]+$`, 'u');
const STANDALONE_EMOJI_RE = new RegExp(`([${SPACES}])[${EMOJI_CHARS}]+(?=[${SPACES}])`, 'gu');
const REPEATED_SPACES_RE = new RegExp(`[${SPACES}]{2,}`, 'g');

const toMessageText = (message) => {
  if (message === null || message === undefined) return '';
  if (typeof message === 'string') return message;
  if (message instanceof Error) return message.message || '';
  if (typeof message === 'object') {
    if (typeof message.message === 'string') return message.message;
    try {
      return JSON.stringify(message);
    } catch {
      return String(message);
    }
  }
  return String(message);
};

const sanitizeLine = (line) =>
  line
    .replace(LEADING_EMOJI_RE, '')
    .replace(TRAILING_EMOJI_RE, '')
    .replace(STANDALONE_EMOJI_RE, '$1')
    .replace(REPEATED_SPACES_RE, ' ')
    .trim();

/**
 * 토스트 문구에서 장식용 이모지를 걷어낸다.
 * - 줄 단위로 앞뒤 이모지를 제거하고, 공백으로 둘러싸인 독립 이모지도 제거한다.
 * - 글자에 붙은 이모지(📎로)는 유지한다.
 * - 이모지만 있던 줄은 버리되, 원래 비어 있던 줄(단락 구분)은 남긴다.
 */
export const sanitizeToastMessage = (message) => {
  const text = toMessageText(message).replace(/\r\n?/g, '\n');
  if (!text.trim()) return '';

  const lines = text.split('\n').map((line) => ({
    cleaned: sanitizeLine(line),
    hadText: line.trim().length > 0,
  }));

  return lines
    .filter(({ cleaned, hadText }) => cleaned.length > 0 || !hadText)
    .map(({ cleaned }) => cleaned)
    .join('\n')
    .replace(/^\n+|\n+$/g, '');
};

/**
 * 자동 닫힘 시간을 정한다. 호출부가 숫자를 넘기면 그대로 쓰고(0 = 유지),
 * 생략하면 타입별 기본값에 문장 길이만큼 읽을 시간을 더한다.
 */
export const resolveToastDuration = (type, duration, message = '') => {
  if (typeof duration === 'number' && Number.isFinite(duration)) {
    return Math.max(0, duration);
  }
  const base = DEFAULT_DURATION_MS[type] ?? DEFAULT_DURATION_MS.info;
  const visibleChars = typeof message === 'string' ? message.replace(/\s+/g, '').length : 0;
  const extra = Math.min(
    READ_TIME_MAX_EXTRA_MS,
    Math.max(0, visibleChars - READ_TIME_GLANCE_CHARS) * READ_TIME_MS_PER_CHAR
  );
  return base + extra;
};

const normalizeAction = (action) => {
  if (!action || typeof action !== 'object') return null;
  const label = typeof action.label === 'string' ? sanitizeToastMessage(action.label) : '';
  if (!label) return null;
  return {
    label,
    onClick: typeof action.onClick === 'function' ? action.onClick : undefined,
  };
};

// ---------------------------------------------------------------------------
// 아이콘 (20px, 타입 색으로 채운 원 + 흰 기호)
// ---------------------------------------------------------------------------

const ICONS = {
  success: (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="9" fill="currentColor" />
      <path
        d="M6.2 10.3l2.5 2.5 5.1-5.4"
        fill="none"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="9" fill="currentColor" />
      <path d="M10 5.6v5.2" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
      <circle cx="10" cy="13.9" r="1.15" fill="#fff" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="9" fill="currentColor" />
      <path d="M10 5.6v5.2" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
      <circle cx="10" cy="13.9" r="1.15" fill="#fff" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <circle cx="10" cy="10" r="9" fill="currentColor" />
      <circle cx="10" cy="6.3" r="1.15" fill="#fff" />
      <path d="M10 9.2v5.2" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" />
    </svg>
  ),
};

const CloseIcon = () => (
  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M4 4l8 8M12 4l-8 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

// ---------------------------------------------------------------------------
// 컨텍스트 / 프로바이더
// ---------------------------------------------------------------------------

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const sequenceRef = useRef(0);
  // 모달이 열려 있는 동안 보관하는 토스트. 마지막 레이어가 닫히면 가장 최근 1개만 보여준다.
  const queueRef = useRef([]);

  const { hasLayer } = useLayer();
  const hasLayerRef = useRef(hasLayer);
  hasLayerRef.current = hasLayer;

  const removeToast = useCallback((id) => {
    queueRef.current = queueRef.current.filter((toast) => toast.id !== id);
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((message, type = 'info', duration, action = null, options = null) => {
    const isElement = React.isValidElement(message);
    const content = isElement ? message : sanitizeToastMessage(message);
    if (!content) return null;

    const resolvedType = TOAST_TYPES.includes(type) ? type : 'info';
    const resolvedOptions = options && typeof options === 'object' ? options : {};
    sequenceRef.current += 1;
    const id = `toast-${Date.now()}-${sequenceRef.current}`;
    const nextToast = {
      id,
      message: content,
      type: resolvedType,
      duration: resolveToastDuration(resolvedType, duration, isElement ? '' : content),
      action: normalizeAction(action),
      createdAt: Date.now(),
      persistAcrossRoute: Boolean(resolvedOptions.persistAcrossRoute),
      replaceKey: typeof resolvedOptions.replaceKey === 'string' && resolvedOptions.replaceKey
        ? resolvedOptions.replaceKey
        : null,
    };
    const keepOthers = (toast) => !nextToast.replaceKey || toast.replaceKey !== nextToast.replaceKey;

    const shouldQueue = hasLayerRef.current
      && resolvedType !== 'error'
      && !resolvedOptions.bypassLayerQueue;
    if (nextToast.replaceKey) {
      queueRef.current = queueRef.current.filter(keepOthers);
    }
    if (shouldQueue) {
      queueRef.current = [...queueRef.current, nextToast];
      if (nextToast.replaceKey) {
        setToasts((prev) => (prev.some((toast) => !keepOthers(toast)) ? prev.filter(keepOthers) : prev));
      }
      return id;
    }

    setToasts((prev) => [...prev.filter(keepOthers), nextToast].slice(-MAX_VISIBLE_TOASTS));
    return id;
  }, []);

  // 마지막 레이어가 닫히면 대기열을 비우고 가장 최근 토스트 1개만 띄운다.
  useEffect(() => {
    if (hasLayer || queueRef.current.length === 0) return;
    const latest = queueRef.current[queueRef.current.length - 1];
    queueRef.current = [];
    setToasts((prev) => [...prev, latest].slice(-MAX_VISIBLE_TOASTS));
  }, [hasLayer]);

  /**
   * 보이는 토스트와 대기열을 비운다 (라우트 전환 등).
   * - includePersistent: persistAcrossRoute 토스트까지 지운다 (기본 false)
   * - keepNewerThanMs  : 이 시간 안에 만든 토스트는 남긴다 ("저장했어요" 직후 navigate 하는 흐름 보호)
   */
  const clearToasts = useCallback(({ includePersistent = false, keepNewerThanMs = 0 } = {}) => {
    const now = Date.now();
    const shouldKeep = (toast) => (
      (!includePersistent && toast.persistAcrossRoute)
      || (keepNewerThanMs > 0 && now - toast.createdAt < keepNewerThanMs)
    );
    queueRef.current = queueRef.current.filter(shouldKeep);
    setToasts((prev) => {
      const next = prev.filter(shouldKeep);
      return next.length === prev.length ? prev : next;
    });
  }, []);

  const contextValue = useMemo(
    () => ({ showToast, removeToast, clearToasts }),
    [clearToasts, removeToast, showToast]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// 컨테이너 / 개별 토스트
// ---------------------------------------------------------------------------

// 라이브 리전은 내용이 들어오기 전에 DOM에 있어야 스크린리더가 안정적으로 읽는다 → 항상 렌더.
const ToastContainer = ({ toasts, onRemove }) => (
  <div className="toast-container" aria-live="polite" aria-relevant="additions">
    {toasts.map((toast) => (
      <Toast key={toast.id} toast={toast} onRemove={() => onRemove(toast.id)} />
    ))}
  </div>
);

const renderMessage = (message) => {
  if (React.isValidElement(message)) return message;
  const lines = String(message).split('\n');
  return lines.map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};

const Toast = ({ toast, onRemove }) => {
  const [exiting, setExiting] = useState(false);
  const [dragY, setDragY] = useState(0);

  const onRemoveRef = useRef(onRemove);
  const exitingRef = useRef(false);
  const autoTimerRef = useRef(null);
  const exitTimerRef = useRef(null);
  const remainingRef = useRef(toast.duration);
  const startedAtRef = useRef(0);
  const touchStartRef = useRef(null);

  const autoDismiss = toast.duration > 0;
  const hasAction = Boolean(toast.action);
  const isAlert = toast.type === 'error' || toast.type === 'warning';

  useEffect(() => {
    onRemoveRef.current = onRemove;
  }, [onRemove]);

  const dismiss = useCallback(() => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    clearTimeout(autoTimerRef.current);
    autoTimerRef.current = null;
    setExiting(true);
    exitTimerRef.current = setTimeout(() => onRemoveRef.current(), EXIT_ANIMATION_MS);
  }, []);

  const startAutoDismiss = useCallback(
    (ms) => {
      clearTimeout(autoTimerRef.current);
      startedAtRef.current = Date.now();
      autoTimerRef.current = setTimeout(dismiss, ms);
    },
    [dismiss]
  );

  const pauseAutoDismiss = useCallback(() => {
    if (!autoDismiss || exitingRef.current || autoTimerRef.current === null) return;
    clearTimeout(autoTimerRef.current);
    autoTimerRef.current = null;
    const elapsed = Date.now() - startedAtRef.current;
    remainingRef.current = Math.max(RESUME_MIN_MS, remainingRef.current - elapsed);
  }, [autoDismiss]);

  const resumeAutoDismiss = useCallback(() => {
    if (!autoDismiss || exitingRef.current || autoTimerRef.current !== null) return;
    startAutoDismiss(remainingRef.current);
  }, [autoDismiss, startAutoDismiss]);

  useEffect(() => {
    if (autoDismiss) startAutoDismiss(toast.duration);
    return () => {
      clearTimeout(autoTimerRef.current);
      clearTimeout(exitTimerRef.current);
    };
  }, [autoDismiss, startAutoDismiss, toast.duration]);

  const handleRootClick = (event) => {
    // 액션이 있는 토스트는 본문 탭으로 닫지 않는다 (실수로 액션을 잃지 않게)
    if (hasAction) return;
    if (event.target.closest?.('button')) return;
    dismiss();
  };

  const handleActionClick = () => {
    toast.action?.onClick?.();
  };

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    pauseAutoDismiss();
  };

  const handleTouchMove = (event) => {
    if (!touchStartRef.current) return;
    const touch = event.touches[0];
    if (!touch) return;
    const dy = touch.clientY - touchStartRef.current.y;
    const dx = touch.clientX - touchStartRef.current.x;
    // 아래(화면 가장자리) 방향으로 끌 때만 따라간다
    setDragY(dy > 0 && dy > Math.abs(dx) ? dy : 0);
  };

  const handleTouchEnd = () => {
    touchStartRef.current = null;
    if (dragY > SWIPE_DISMISS_PX) {
      dismiss();
      return;
    }
    setDragY(0);
    resumeAutoDismiss();
  };

  const className = [
    'toast',
    `toast-${toast.type}`,
    hasAction ? 'toast-has-action' : '',
    exiting ? 'toast-exiting' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const dragStyle = dragY
    ? {
        transform: `translateY(${dragY}px)`,
        opacity: Math.max(0.35, 1 - dragY / 140),
        transition: 'none',
      }
    : undefined;

  return (
    <div
      className={className}
      role={isAlert ? 'alert' : 'status'}
      aria-live={isAlert ? 'assertive' : 'polite'}
      aria-atomic="true"
      style={dragStyle}
      onClick={handleRootClick}
      onMouseEnter={pauseAutoDismiss}
      onMouseLeave={resumeAutoDismiss}
      onFocus={pauseAutoDismiss}
      onBlur={resumeAutoDismiss}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      <span className="toast-icon" aria-hidden="true" data-testid={`toast-icon-${toast.type}`}>
        {ICONS[toast.type] || ICONS.info}
      </span>
      <span className="toast-message">{renderMessage(toast.message)}</span>
      {hasAction && (
        <button type="button" className="toast-action" onClick={handleActionClick}>
          {toast.action.label}
        </button>
      )}
      {(hasAction || !autoDismiss) && (
        <button type="button" className="toast-close" onClick={dismiss} aria-label="알림 닫기">
          <CloseIcon />
        </button>
      )}
    </div>
  );
};

export default Toast;
